import { colors } from "@fraction/shared";
import { memo } from "react";
import { BrandedLogo } from "src/components/BrandLogo";
import config from "src/config";
import { Mail, Phone } from "src/icons";
import { cn } from "src/utilities/shadcnUtils";

import Divider from "../Divider";
import FooterDisclosure from "../disclosures/FooterDisclosure";
import TextLink from "../TextLink";
import { MediumText, RegularText, SemiBoldText } from "../v1/Text";

interface PageFooterProps {
  className?: string;
  columnsToShow?: FooterColumn[];
}

export enum FooterColumn {
  FINANCING = "financing",
  COMPANY = "company",
  RESOURCES = "resources",
  PARTNER = "partner",
  LEGAL = "legal",
}

const STYLES = {
  container: "block pt-20 px-[200px] max-[1120px]:px-[15px] max-[1120px]:pt-[135px]",
  row: "flex flex-row",
  topRow: "justify-between flex flex-row flex-wrap",
  column: "flex flex-col mr-[50px]",
  logoColumn: "flex flex-col",
  linkText: "text-sm",
  icon: "mr-3",
  contactContainer: "mb-[14px] items-center",
  header: "text-base max-[748px]:mt-[30px]",
  directoryText: "mb-[18px] text-sm",
  divider: "mt-10 mb-10",
  directoryContainer: "flex flex-row flex-wrap",
  logo: "mb-10",
  textLink: "text-green-500 hover:text-green-300 visited:text-green-700 visited:hover:text-green-300",
};

const brandURL = (path: string) => `${config.brandPublicURL}${path}`;

const PageFooter = ({
  className,
  columnsToShow = [FooterColumn.RESOURCES, FooterColumn.LEGAL],
}: PageFooterProps) => (
  <div className={cn(STYLES.container, className)}>
    <div className={STYLES.topRow}>
      <div className={STYLES.logoColumn}>
        <BrandedLogo className={STYLES.logo} />
        <div className={STYLES.column}>
          {config.brandSupportPhone ? (
            <div className={cn(STYLES.row, STYLES.contactContainer)}>
              <Phone className={STYLES.icon} color={colors.palette.GREY_800} />
              <MediumText className={STYLES.linkText}>
                <TextLink className={STYLES.textLink} href={`tel:${config.brandSupportPhone}`}>
                  {config.brandSupportPhone}
                </TextLink>
              </MediumText>
            </div>
          ) : null}
          <div className={cn(STYLES.row, STYLES.contactContainer)}>
            <Mail className={STYLES.icon} color={colors.palette.GREY_800} />
            <MediumText className={STYLES.linkText}>
              <TextLink className={STYLES.textLink} href={`mailto:${config.brandContactEmail}`}>
                {config.brandContactEmail}
              </TextLink>
            </MediumText>
          </div>
        </div>
      </div>
      <div className={STYLES.directoryContainer}>
        {columnsToShow.includes(FooterColumn.FINANCING) && (
          <div className={STYLES.column}>
            <SemiBoldText className={cn(STYLES.directoryText, STYLES.header)}>Financing</SemiBoldText>
            <RegularText className={STYLES.directoryText}>
              <TextLink href={brandURL("/the-cost")}>How it works</TextLink>
            </RegularText>
            <RegularText className={STYLES.directoryText}>
              <TextLink href="/application">Apply now</TextLink>
            </RegularText>
          </div>
        )}
        {columnsToShow.includes(FooterColumn.COMPANY) && (
          <div className={STYLES.column}>
            <SemiBoldText className={cn(STYLES.directoryText, STYLES.header)}>Company</SemiBoldText>
            <RegularText className={STYLES.directoryText}>
              <TextLink href={brandURL("/our-story")}>Our story</TextLink>
            </RegularText>
            <RegularText className={STYLES.directoryText}>
              <TextLink href={brandURL("/careers")}>Careers</TextLink>
            </RegularText>
            <RegularText className={STYLES.directoryText}>
              <TextLink href={brandURL("/contact-us")}>Contact us</TextLink>
            </RegularText>
          </div>
        )}
        {columnsToShow.includes(FooterColumn.RESOURCES) && (
          <div className={STYLES.column}>
            <SemiBoldText className={cn(STYLES.directoryText, STYLES.header)}>Resources</SemiBoldText>
            <RegularText className={STYLES.directoryText}>
              <TextLink href={brandURL("/why-fraction")}>Why {config.brandName}?</TextLink>
            </RegularText>
            <RegularText className={STYLES.directoryText}>
              <TextLink href={brandURL("/faqs")}>FAQ</TextLink>
            </RegularText>
            <RegularText className={STYLES.directoryText}>
              <TextLink href={brandURL("/blog")}>Blog</TextLink>
            </RegularText>
          </div>
        )}
        {columnsToShow.includes(FooterColumn.PARTNER) && (
          <div className={STYLES.column}>
            <SemiBoldText className={cn(STYLES.directoryText, STYLES.header)}>Partner</SemiBoldText>
            <RegularText className={STYLES.directoryText}>
              <TextLink href={brandURL("/partner-with-us")}>Brokers</TextLink>
            </RegularText>
          </div>
        )}
        {columnsToShow.includes(FooterColumn.LEGAL) && (
          <div className={STYLES.column}>
            <SemiBoldText className={cn(STYLES.directoryText, STYLES.header)}>Legal</SemiBoldText>
            <RegularText className={STYLES.directoryText}>
              <TextLink href={brandURL("/usa/privacy-policy")}>Privacy Policy</TextLink>
            </RegularText>
            <RegularText className={STYLES.directoryText}>
              <TextLink href={brandURL("/terms-of-use")}>Terms of Use</TextLink>
            </RegularText>
            <RegularText className={STYLES.directoryText}>
              <TextLink href="https://nmlsconsumeraccess.org/EntityDetails.aspx/COMPANY/2084341">
                NMLS Consumer Access
              </TextLink>
            </RegularText>
            <RegularText className={STYLES.directoryText}>
              <TextLink href={brandURL("/licenses")}>Licensing</TextLink>
            </RegularText>
          </div>
        )}
      </div>
    </div>
    <Divider className={STYLES.divider} />
    <FooterDisclosure />
  </div>
);

export default memo(PageFooter);
